import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, LayoutGrid, List, Monitor, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DataTable, { Column } from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import { fetchHosts } from '../lib/api';
import { useTenant } from '../contexts/TenantContext';
import { useAutoRefresh } from '../contexts/AutoRefreshContext';
import { format, differenceInHours } from 'date-fns';

interface HostEntry {
  fleet_id: number;
  hostname: string;
  platform: string;
  os_version: string;
  fleet_status: string;
  wazuh_status: string;
  wazuh_id: string;
  last_seen: string;
}

function lastSeenColor(lastSeen: string): string {
  if (!lastSeen) return 'text-gray-400 dark:text-gray-500';
  const hours = differenceInHours(new Date(), new Date(lastSeen));
  if (hours < 1) return 'text-green-600 dark:text-green-400';
  if (hours < 24) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-500 dark:text-red-400';
}

function platformIcon(platform: string) {
  const p = platform.toLowerCase();
  if (p.includes('windows')) return '🪟';
  if (p.includes('darwin') || p.includes('macos')) return '🍎';
  if (p.includes('linux') || p.includes('ubuntu')) return '🐧';
  return '💻';
}

function HostCard({ host, onClick }: { host: HostEntry; onClick: () => void }) {
  const { t } = useTranslation();
  const hoursAgo = host.last_seen ? differenceInHours(new Date(), new Date(host.last_seen)) : null;
  const lastSeenText = host.last_seen ? format(new Date(host.last_seen), 'MMM d, HH:mm') : '-';

  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{platformIcon(host.platform)}</span>
          <div>
            <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{host.hostname}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{host.os_version}</p>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <Monitor className="w-3 h-3" /> {t('hosts.fleet')}
          </span>
          <StatusBadge status={host.fleet_status} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <Shield className="w-3 h-3" /> {t('hosts.wazuh')}
          </span>
          {host.wazuh_status ? <StatusBadge status={host.wazuh_status} /> : <span className="text-xs text-gray-400">-</span>}
        </div>
        <div className="flex items-center justify-between pt-1 border-t border-gray-100 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400">{t('hosts.lastSeen')}</span>
          <span className={`text-xs font-medium ${lastSeenColor(host.last_seen)}`}>
            {lastSeenText}
            {hoursAgo !== null && (
              <span className="ml-1 text-gray-400 dark:text-gray-500">
                ({hoursAgo < 1 ? '<1h' : `${hoursAgo}h`})
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function HostsPage() {
  const [hosts, setHosts] = useState<HostEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline'>('all');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>(() =>
    (localStorage.getItem('secuguard_hosts_view') as 'table' | 'grid') || 'table'
  );
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedTenantId } = useTenant();
  const { subscribe } = useAutoRefresh();

  const loadData = useCallback(() => {
    setLoading(true);
    fetchHosts(selectedTenantId || undefined)
      .then(setHosts)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedTenantId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => subscribe(loadData), [subscribe, loadData]);

  const toggleView = (mode: 'table' | 'grid') => {
    setViewMode(mode);
    localStorage.setItem('secuguard_hosts_view', mode);
  };

  const filtered = useMemo(() => {
    let result = hosts;
    if (statusFilter !== 'all') {
      result = result.filter((h) => {
        if (!h.last_seen) return statusFilter === 'offline';
        const hours = differenceInHours(new Date(), new Date(h.last_seen));
        return statusFilter === 'online' ? hours < 1 : hours >= 1;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (h) =>
          h.hostname.toLowerCase().includes(q) ||
          h.platform.toLowerCase().includes(q) ||
          h.os_version.toLowerCase().includes(q)
      );
    }
    return result;
  }, [hosts, search, statusFilter]);

  const onlineCount = useMemo(() => hosts.filter((h) => h.last_seen && differenceInHours(new Date(), new Date(h.last_seen)) < 1).length, [hosts]);

  const columns: Column<HostEntry>[] = [
    { key: 'hostname', header: t('hosts.hostname') },
    { key: 'platform', header: t('hosts.platform') },
    { key: 'os_version', header: t('hosts.osVersion') },
    {
      key: 'fleet_status',
      header: t('hosts.fleet'),
      render: (h) => <StatusBadge status={h.fleet_status} />,
    },
    {
      key: 'wazuh_status',
      header: t('hosts.wazuh'),
      render: (h) =>
        h.wazuh_status ? <StatusBadge status={h.wazuh_status} /> : <span className="text-gray-400 dark:text-gray-500">-</span>,
    },
    {
      key: 'last_seen',
      header: t('hosts.lastSeen'),
      render: (h) =>
        h.last_seen ? (
          <span className={lastSeenColor(h.last_seen)}>{format(new Date(h.last_seen), 'MMM d, HH:mm')}</span>
        ) : '-',
    },
  ];

  if (loading) {
    return <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('hosts.searchHosts')}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-1">
          {([
            { key: 'all' as const, label: t('common.all'), count: hosts.length },
            { key: 'online' as const, label: t('hosts.online'), count: onlineCount },
            { key: 'offline' as const, label: t('hosts.offline'), count: hosts.length - onlineCount },
          ]).map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                statusFilter === f.key
                  ? f.key === 'online' ? 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/40 dark:text-green-400 dark:border-green-700'
                    : f.key === 'offline' ? 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-400 dark:border-red-700'
                    : 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-400 dark:border-blue-700'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>
        <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleView('table')}
            className={`p-1.5 ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            title={t('hosts.tableView')}
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => toggleView('grid')}
            className={`p-1.5 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            title={t('hosts.gridView')}
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('hosts.hostCount', { count: filtered.length })}</p>
      </div>

      {viewMode === 'table' ? (
        <DataTable
          data={filtered}
          columns={columns}
          keyExtractor={(h) => h.fleet_id}
          onRowClick={(h) => navigate(`/hosts/${h.fleet_id}`)}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.length === 0 ? (
            <p className="col-span-full text-center text-gray-500 dark:text-gray-400 py-12">{t('common.noData')}</p>
          ) : (
            filtered.map((h) => (
              <HostCard key={h.fleet_id} host={h} onClick={() => navigate(`/hosts/${h.fleet_id}`)} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
